import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { toast } from 'react-toastify';
import { useFormatMoneda } from '../utils/moneda';

export default function NuevaVenta() {
  const navigate = useNavigate();
  const formatMoneda = useFormatMoneda();
  const [paso, setPaso] = useState(1); // 1=cliente, 2=productos, 3=crédito

  // Paso 1 — cliente
  const [clientes, setClientes] = useState([]);
  const [buscarCliente, setBuscarCliente] = useState('');
  const [clienteSeleccionado, setClienteSeleccionado] = useState(null);

  // Paso 2 — productos
  const [productos, setProductos] = useState([]);
  const [buscarProducto, setBuscarProducto] = useState('');
  const [carrito, setCarrito] = useState([]); // [{ producto, cantidad }]

  // Paso 3 — crédito
  const [creditoForm, setCreditoForm] = useState({
    tasa_interes: 0,
    num_cuotas: '',
    frecuencia: 'mensual',
    fecha_inicio: new Date().toISOString().split('T')[0],
    observaciones: '',
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get('/clientes', { params: { activo: 1 } }).then(r => setClientes(r.data)).catch(() => {});
    api.get('/productos').then(r => setProductos(r.data)).catch(() => {});
  }, []);

  const clientesFiltrados = clientes.filter(c =>
    c.nombre.toLowerCase().includes(buscarCliente.toLowerCase()) ||
    c.documento.includes(buscarCliente)
  );

  const productosFiltrados = productos.filter(p =>
    p.nombre.toLowerCase().includes(buscarProducto.toLowerCase()) ||
    (p.referencia || '').toLowerCase().includes(buscarProducto.toLowerCase())
  );

  const totalCarrito = carrito.reduce((sum, i) => sum + i.producto.precio * i.cantidad, 0);

  function agregarAlCarrito(producto) {
    if (producto.stock === 0) { toast.warning('Sin stock disponible'); return; }
    setCarrito(prev => {
      const existe = prev.find(i => i.producto.id === producto.id);
      if (existe) {
        if (existe.cantidad >= producto.stock) { toast.warning('No hay más stock disponible'); return prev; }
        return prev.map(i => i.producto.id === producto.id ? { ...i, cantidad: i.cantidad + 1 } : i);
      }
      return [...prev, { producto, cantidad: 1 }];
    });
  }

  function cambiarCantidad(productoId, cantidad) {
    const num = parseInt(cantidad);
    if (isNaN(num) || num < 1) return;
    const item = carrito.find(i => i.producto.id === productoId);
    if (num > item.producto.stock) { toast.warning('Cantidad supera el stock disponible'); return; }
    setCarrito(prev => prev.map(i => i.producto.id === productoId ? { ...i, cantidad: num } : i));
  }

  function quitarDelCarrito(productoId) {
    setCarrito(prev => prev.filter(i => i.producto.id !== productoId));
  }

  async function confirmarVenta() {
    if (!creditoForm.num_cuotas || parseInt(creditoForm.num_cuotas) < 1) {
      toast.error('Ingresa el número de cuotas');
      return;
    }
    setLoading(true);
    try {
      const res = await api.post('/ventas', {
        cliente_id: clienteSeleccionado.id,
        items: carrito.map(i => ({ producto_id: i.producto.id, cantidad: i.cantidad })),
        tasa_interes: parseFloat(creditoForm.tasa_interes) || 0,
        num_cuotas: parseInt(creditoForm.num_cuotas),
        frecuencia: creditoForm.frecuencia,
        fecha_inicio: creditoForm.fecha_inicio,
        observaciones: creditoForm.observaciones,
      });
      toast.success(`Venta #${res.data.venta_id} creada — ${res.data.num_cuotas} cuota(s) generadas`);
      navigate('/ventas');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al crear la venta');
    } finally { setLoading(false); }
  }

  const cuotaEstimada = useCallback(() => {
    if (!creditoForm.num_cuotas || !totalCarrito) return null;
    const total = totalCarrito * (1 + parseFloat(creditoForm.tasa_interes || 0) / 100);
    return (total / parseInt(creditoForm.num_cuotas)).toFixed(0);
  }, [totalCarrito, creditoForm.tasa_interes, creditoForm.num_cuotas]);

  return (
    <div style={{ maxWidth: 900, margin: '0 auto' }}>
      {/* Header + pasos */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 28 }}>
        <button onClick={() => navigate('/ventas')} style={{ background: 'none', border: 'none', color: 'var(--text-2)', cursor: 'pointer', fontSize: 22 }}>←</button>
        <h1 style={{ margin: 0, fontSize: '1.4rem', fontWeight: 700 }}>Nueva Venta a Crédito</h1>
      </div>

      {/* Barra de pasos */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 28, borderRadius: 10, overflow: 'hidden', background: 'var(--bg-card)' }}>
        {[['1', 'Cliente'], ['2', 'Productos'], ['3', 'Crédito']].map(([n, label]) => (
          <div key={n} style={{ flex: 1, padding: '12px 0', textAlign: 'center', background: paso >= parseInt(n) ? '#0ea5e9' : 'transparent', color: paso >= parseInt(n) ? '#fff' : '#475569', fontWeight: 600, fontSize: 14, transition: 'background 0.2s' }}>
            {n}. {label}
          </div>
        ))}
      </div>

      {/* ── PASO 1: Seleccionar cliente ── */}
      {paso === 1 && (
        <div style={styles.card}>
          <h2 style={styles.cardTitle}>Selecciona el cliente</h2>
          <input
            placeholder="Buscar por nombre o documento..."
            value={buscarCliente}
            onChange={e => setBuscarCliente(e.target.value)}
            style={{ ...styles.input, marginBottom: 16 }}
            autoFocus
          />
          <div style={{ maxHeight: 400, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {clientesFiltrados.slice(0, 30).map(c => (
              <div
                key={c.id}
                onClick={() => setClienteSeleccionado(c)}
                style={{
                  padding: '12px 16px', borderRadius: 10, cursor: 'pointer', border: '2px solid',
                  borderColor: clienteSeleccionado?.id === c.id ? '#0ea5e9' : '#1e3a5f',
                  background: clienteSeleccionado?.id === c.id ? '#0ea5e922' : '#0f172a',
                  transition: 'all 0.15s',
                }}
              >
                <div style={{ fontWeight: 600 }}>{c.nombre}</div>
                <div style={{ fontSize: 12, color: 'var(--text-3)' }}>{c.tipo_documento}: {c.documento} {c.telefono ? `· ${c.telefono}` : ''}</div>
              </div>
            ))}
            {clientesFiltrados.length === 0 && <p style={{ color: 'var(--text-4)', textAlign: 'center' }}>No se encontraron clientes</p>}
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
            <button disabled={!clienteSeleccionado} onClick={() => setPaso(2)} style={{ ...styles.btn, opacity: clienteSeleccionado ? 1 : 0.4 }}>
              Continuar →
            </button>
          </div>
        </div>
      )}

      {/* ── PASO 2: Agregar productos ── */}
      {paso === 2 && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 20 }}>
          {/* Catálogo */}
          <div style={styles.card}>
            <h2 style={styles.cardTitle}>Catálogo de productos</h2>
            <input
              placeholder="Buscar producto..."
              value={buscarProducto}
              onChange={e => setBuscarProducto(e.target.value)}
              style={{ ...styles.input, marginBottom: 12 }}
            />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 420, overflowY: 'auto' }}>
              {productosFiltrados.map(p => (
                <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: 'var(--bg-app)', borderRadius: 8, opacity: p.stock === 0 ? 0.5 : 1 }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{p.nombre}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-3)' }}>{p.referencia ? `Ref: ${p.referencia} · ` : ''}Stock: {p.stock}</div>
                    <div style={{ color: '#34d399', fontWeight: 700, fontSize: 13 }}>{formatMoneda(p.precio)}</div>
                  </div>
                  <button onClick={() => agregarAlCarrito(p)} disabled={p.stock === 0} style={{ ...styles.btnSm, opacity: p.stock === 0 ? 0.4 : 1 }}>+ Agregar</button>
                </div>
              ))}
              {productosFiltrados.length === 0 && <p style={{ color: 'var(--text-4)', textAlign: 'center' }}>Sin productos</p>}
            </div>
          </div>

          {/* Carrito */}
          <div style={styles.card}>
            <h2 style={styles.cardTitle}>Resumen</h2>
            <div style={{ fontSize: 13, color: 'var(--text-3)', marginBottom: 12 }}>Cliente: <strong style={{ color: 'var(--text-1s)' }}>{clienteSeleccionado?.nombre}</strong></div>
            {carrito.length === 0 && <p style={{ color: 'var(--text-4)', fontSize: 13 }}>Agrega productos al carrito</p>}
            {carrito.map(item => (
              <div key={item.producto.id} style={{ padding: '8px 0', borderBottom: '1px solid #1e293b' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                  <div style={{ flex: 1, fontSize: 13, fontWeight: 600 }}>{item.producto.nombre}</div>
                  <button onClick={() => quitarDelCarrito(item.producto.id)} style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', fontSize: 16, padding: 2 }}>×</button>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 12, color: 'var(--text-3)' }}>Cant:</span>
                    <input
                      type="number" min="1" max={item.producto.stock}
                      value={item.cantidad}
                      onChange={e => cambiarCantidad(item.producto.id, e.target.value)}
                      style={{ width: 52, background: 'var(--bg-app)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text-1)', padding: '2px 6px', fontSize: 13 }}
                    />
                  </div>
                  <span style={{ fontSize: 13, color: '#34d399', fontWeight: 700 }}>{formatMoneda(item.producto.precio * item.cantidad)}</span>
                </div>
              </div>
            ))}
            {carrito.length > 0 && (
              <div style={{ marginTop: 12, paddingTop: 12, borderTop: '2px solid var(--border)', display: 'flex', justifyContent: 'space-between', fontWeight: 700 }}>
                <span>Total</span>
                <span style={{ color: '#38bdf8', fontSize: 18 }}>{formatMoneda(totalCarrito)}</span>
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, marginTop: 20 }}>
              <button onClick={() => setPaso(1)} style={styles.btnSecundario}>← Volver</button>
              <button disabled={carrito.length === 0} onClick={() => setPaso(3)} style={{ ...styles.btn, flex: 1, opacity: carrito.length === 0 ? 0.4 : 1 }}>Continuar →</button>
            </div>
          </div>
        </div>
      )}

      {/* ── PASO 3: Configurar crédito ── */}
      {paso === 3 && (
        <div style={styles.card}>
          <h2 style={styles.cardTitle}>Condiciones del crédito</h2>

          {/* Resumen de la venta */}
          <div style={{ background: 'var(--bg-app)', borderRadius: 10, padding: 16, marginBottom: 20 }}>
            <div style={{ fontSize: 13, color: 'var(--text-3)', marginBottom: 4 }}>Cliente: <strong style={{ color: 'var(--text-1s)' }}>{clienteSeleccionado?.nombre}</strong></div>
            <div style={{ fontSize: 13, color: 'var(--text-3)', marginBottom: 4 }}>Productos: <strong style={{ color: 'var(--text-1s)' }}>{carrito.length} ítems</strong></div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, paddingTop: 8, borderTop: '1px solid #1e293b' }}>
              <span style={{ color: 'var(--text-2)', fontWeight: 600 }}>Monto a financiar</span>
              <span style={{ color: '#38bdf8', fontSize: 20, fontWeight: 700 }}>{formatMoneda(totalCarrito)}</span>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div>
              <label style={styles.label}>Número de cuotas *</label>
              <input
                required type="number" min="1"
                value={creditoForm.num_cuotas}
                onChange={e => setCreditoForm(f => ({ ...f, num_cuotas: e.target.value }))}
                style={styles.input}
                placeholder="Ej: 12"
              />
            </div>
            <div>
              <label style={styles.label}>Frecuencia</label>
              <select value={creditoForm.frecuencia} onChange={e => setCreditoForm(f => ({ ...f, frecuencia: e.target.value }))} style={styles.input}>
                <option value="diario">Diario</option>
                <option value="semanal">Semanal</option>
                <option value="quincenal">Quincenal</option>
                <option value="mensual">Mensual</option>
              </select>
            </div>
            <div>
              <label style={styles.label}>Tasa de interés (%)</label>
              <input
                type="number" min="0" step="0.1"
                value={creditoForm.tasa_interes}
                onChange={e => setCreditoForm(f => ({ ...f, tasa_interes: e.target.value }))}
                style={styles.input}
              />
            </div>
            <div>
              <label style={styles.label}>Fecha de inicio</label>
              <input
                type="date"
                value={creditoForm.fecha_inicio}
                onChange={e => setCreditoForm(f => ({ ...f, fecha_inicio: e.target.value }))}
                style={styles.input}
              />
            </div>
          </div>

          {cuotaEstimada() && (
            <div style={{ background: '#0f3d2e', borderRadius: 10, padding: '12px 16px', marginTop: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ color: '#34d399', fontSize: 14 }}>Cuota estimada por período</span>
              <span style={{ color: '#34d399', fontSize: 22, fontWeight: 700 }}>{formatMoneda(parseInt(cuotaEstimada()))}</span>
            </div>
          )}

          <div style={{ marginTop: 16 }}>
            <label style={styles.label}>Observaciones</label>
            <textarea
              value={creditoForm.observaciones}
              onChange={e => setCreditoForm(f => ({ ...f, observaciones: e.target.value }))}
              style={{ ...styles.input, minHeight: 60, resize: 'vertical' }}
              placeholder="Notas adicionales..."
            />
          </div>

          <div style={{ display: 'flex', gap: 8, marginTop: 20, justifyContent: 'flex-end' }}>
            <button onClick={() => setPaso(2)} style={styles.btnSecundario}>← Volver</button>
            <button onClick={confirmarVenta} disabled={loading} style={{ ...styles.btn, minWidth: 180 }}>
              {loading ? 'Creando venta...' : '✓ Confirmar venta'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  card: { background: 'var(--bg-card)', borderRadius: 14, padding: 24 },
  cardTitle: { margin: '0 0 16px', fontSize: '1.1rem', fontWeight: 700 },
  btn: { background: '#0ea5e9', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 22px', cursor: 'pointer', fontWeight: 600, fontSize: 14 },
  btnSecundario: { background: 'var(--bg-card)', color: 'var(--text-2)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 18px', cursor: 'pointer', fontSize: 14 },
  btnSm: { background: '#0f3d2e', color: '#34d399', border: 'none', borderRadius: 6, padding: '6px 12px', cursor: 'pointer', fontSize: 12, fontWeight: 600 },
  input: { width: '100%', background: 'var(--bg-app)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 12px', color: 'var(--text-1)', fontSize: 14, boxSizing: 'border-box' },
  label: { display: 'block', color: 'var(--text-2)', fontSize: 12, marginBottom: 4 },
};
